/**
 * useCanvasAutoSave — Debounced persistence of canvas interaction state.
 *
 * Watches nodes, edges, clusters, and dismissed nodes for changes
 * and auto-saves to the backend via PUT /canvas/:sessionId.
 * Debounced at 1 second to avoid excessive API calls during rapid changes.
 */

import { useEffect, useRef } from 'react';
import type { Edge } from '@xyflow/react';
import type { CanvasCluster, CanvasState, DismissedCanvasNode } from '@shared/types.ts';
import { mapToDisplayType, deriveSubType } from '@shared/canvas-type-map.ts';
import type { CanvasFlowNode } from './useProcessCanvas';
import { saveCanvasState } from '@/services/api';

const AUTO_SAVE_DELAY_MS = 1000;

/**
 * Build a CanvasState snapshot from the current frontend state.
 */
function buildCanvasStateSnapshot(
  nodes: CanvasFlowNode[],
  edges: Edge[],
  clusters: CanvasCluster[],
  dismissedNodes: DismissedCanvasNode[],
): CanvasState {
  return {
    nodes: nodes
      .filter((n) => n.type !== 'sectionGroup')
      .map((n) => ({
        id: n.id,
        type: mapToDisplayType(n.data.canvasNodeType),
        name: n.data.label,
        description: n.data.description,
        position: n.position,
        sourceType: n.data.sourceType,
        subType: n.data.subType ?? deriveSubType(n.data.canvasNodeType),
        section: n.data.section,
        stepNumber: n.data.stepNumber,
        edgeCount: n.data.edgeCount,
        conflictNew: n.data.conflictNew,
        conflictExisting: n.data.conflictExisting,
        sourceName: n.data.sourceName,
        sourceDate: n.data.sourceDate,
        staged: n.data.staged || undefined,
        additionalContext: n.data.additionalContext,
      })),
    edges: edges.map((e) => ({
      id: e.id,
      type: ((e.data?.canonicalType as string) ?? 'RELATES_TO') as import('@shared/types.ts').CanvasEdgeType,
      sourceId: e.source,
      targetId: e.target,
    })),
    clusters: clusters.length > 0 ? clusters : undefined,
    dismissedNodes: dismissedNodes.length > 0 ? dismissedNodes : undefined,
    lastUpdatedAt: new Date().toISOString(),
    version: 1,
  };
}

export function useCanvasAutoSave(
  sessionId: string,
  nodes: CanvasFlowNode[],
  edges: Edge[],
  clusters: CanvasCluster[],
  dismissedNodes: DismissedCanvasNode[],
  isLoading: boolean,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip auto-save while loading initial state
    if (isLoading) return;

    // Skip the very first render after loading (the initial state is already persisted)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Debounce: clear previous timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      const snapshot = buildCanvasStateSnapshot(nodes, edges, clusters, dismissedNodes);
      void saveCanvasState(sessionId, snapshot);
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [sessionId, nodes, edges, clusters, dismissedNodes, isLoading]);
}
